import { Box, Loader, Popover, useMantineTheme } from "@mantine/core";
import { useMergedRef } from "@mantine/hooks";
import React from "react";
import { IconV2, RpiDrawerInputProps, RpiDrawerLabel, RpiText, useRpiDrawerInputStyles } from "../../..";
import { Flex } from "../../RpiFlex";

export interface RpiDrawerSelectElement<T = unknown> {
  label: string;
  value: string | number;
  data?: T;
  labelRender?: React.ReactNode;
}

type RpiDrawerSelectInputProps = RpiDrawerInputProps &
  Omit<React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, "onChange" | "value"> & {
    data: RpiDrawerSelectElement[];
    onChange?: (
      value: RpiDrawerSelectElement["value"] | undefined,
      element: RpiDrawerSelectElement | undefined
    ) => void;
    value?: RpiDrawerSelectElement["value"];
    searchable?: boolean;
    onSearch?: (value: string) => void;
    isLoading?: boolean;
  };

export const RpiDrawerSelectInput = React.forwardRef<HTMLInputElement, RpiDrawerSelectInputProps>(
  (
    {
      label,
      error,
      fullWidth,
      classNames,
      styles: initialStyles,
      data,

      onChange,
      value,
      searchable,
      onSearch,
      isLoading,
      ...inputProps
    },
    ref
  ) => {
    const theme = useMantineTheme();

    const [opened, setOpened] = React.useState<boolean>(false);
    const [searchValue, setSearchValue] = React.useState("");

    const selected = React.useMemo(() => data.find((e) => e.value === value), [data, value]);

    const { classes, cx } = useRpiDrawerInputStyles({
      isError: !!error,
      isDisabled: inputProps.disabled,
      isEmpty: opened ? false : undefined,
    });

    const styles = React.useMemo(() => {
      if (!initialStyles) return {};
      return typeof initialStyles === "function" ? initialStyles(theme) : initialStyles;
    }, [initialStyles]);

    const inputRef = React.useRef<HTMLInputElement>();
    const refs = useMergedRef<any>(inputRef, ref);

    React.useEffect(() => {
      if (selected) {
        setSearchValue(selected.label);
      }
    }, [selected]);

    const handleSetSelected = (element: typeof selected, updateSearch = true) => {
      if (updateSearch) {
        setSearchValue(element?.label || "");
      }

      onChange && onChange(element?.value, element);
    };

    const handleSetSearchValue = (value: typeof searchValue) => {
      if (selected) handleSetSelected(undefined, false);

      setSearchValue(value);
      onSearch && onSearch(value);
    };

    React.useEffect(() => {
      if (searchable && !opened) {
        if (searchValue && (!selected || selected.label !== searchValue)) {
          handleSetSearchValue("");
        }
      }
    }, [opened, searchable, selected, searchValue, handleSetSearchValue]);

    return (
      <Flex.Column gap={8} fullWidth={fullWidth}>
        <Popover
          opened={opened}
          onChange={setOpened}
          offset={0}
          width="target"
          position="bottom"
          styles={(theme) => ({
            dropdown: {
              borderRadius: 8,
              border: "none",
              boxShadow: theme.other.boxShadows.large,
              padding: 3,
            },
          })}
        >
          <Popover.Target>
            <Box
              className={cx(classes.root, classNames?.root)}
              sx={styles.root}
              onClick={() => {
                if (!opened) setOpened(!opened);
                inputRef.current?.focus();
              }}
            >
              <Flex.Row fullWidth sx={{ height: "100%", cursor: `${searchable ? "text" : "pointer"} !important` }}>
                <Flex.Column justify="flex-end" sx={{ flex: 1 }}>
                  <Box
                    ref={refs as any}
                    component="input"
                    type={searchable ? "text" : "search"}
                    className={classes.input}
                    sx={[
                      styles.input,
                      {
                        cursor: `${searchable ? "text" : "pointer"} !important`,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      },
                    ]}
                    placeholder={label || "placeholder"}
                    disabled={!searchable}
                    value={searchValue}
                    {...inputProps}
                    onChange={searchable ? (e: any) => handleSetSearchValue(e.target.value) : undefined}
                  />

                  {label && (
                    <Box component="label" className={cx(classes.label, classNames?.label)} sx={styles.label}>
                      <RpiDrawerLabel label={label} required={inputProps.required} />
                    </Box>
                  )}
                </Flex.Column>

                <Flex.Column justify="center" sx={(theme) => ({ color: theme.colors.brand[5], padding: 8 })}>
                  <IconV2.ArrowDown
                    color="currentColor"
                    size={{ width: 10 }}
                    style={{ transform: opened ? "rotate(180deg)" : "" }}
                  />
                </Flex.Column>
              </Flex.Row>
            </Box>
          </Popover.Target>
          <Popover.Dropdown>
            <Flex.Column
              sx={(theme) => ({
                width: "100%",
                height: "100%",
                maxHeight: 224,
                minHeight: 32,
                overflowX: "hidden",
                overflowY: "auto",
                color: theme.colors.brand[5],
                transition: "color 250ms",
                "&:hover": {
                  color: theme.colors.text[5],
                },
              })}
            >
              {!isLoading && (
                <>
                  {data.map((element, i) => {
                    return (
                      <Flex.Row
                        key={`select-element-${i}-${element.value}`}
                        align="center"
                        onClick={() => {
                          handleSetSelected(element);
                          setOpened(false);
                        }}
                        sx={(theme) => ({
                          borderRadius: 8,
                          padding: "8px 24px",
                          height: 32,
                          cursor: "pointer",
                          color: "inherit",
                          flexShrink: 0,
                          "&:hover": {
                            background: theme.colors.calm[4],
                          },
                        })}
                      >
                        <RpiText
                          type="p3"
                          weight="light"
                          ellipsis
                          color="inherit"
                          sx={{ width: "100%" }}
                          children={element.labelRender || element.label}
                        />
                      </Flex.Row>
                    );
                  })}

                  {data.length === 0 && (
                    <Flex.Row justify="center" align="center" sx={{ flex: 1 }}>
                      <RpiText type="p3" weight="light" color={(theme) => theme.colors.brand[3]} children="No data" />
                    </Flex.Row>
                  )}
                </>
              )}

              {isLoading && (
                <Flex.Row justify="center" align="center" sx={{ height: 64 }}>
                  <Loader size="md" />
                </Flex.Row>
              )}
            </Flex.Column>
          </Popover.Dropdown>
        </Popover>

        {error && <Box className={cx(classes.error, classNames?.error)} sx={styles.error} children={error} />}
      </Flex.Column>
    );
  }
);
